/**
 * Quantities as people read them. They arrive as decimal strings in base
 * units ('240', '12.5'); a quantity is never money, so these helpers are
 * deliberately not in vouchers/money.ts.
 */
import type { InvItem, InvUnit } from '../api/types'

/** '1250' -> '1,250'; '12.500' -> '12.5'. */
export function fmtQty(v: string | number | null | undefined): string {
  const n = Number(v ?? 0)
  if (!Number.isFinite(n)) return String(v ?? '')
  return n.toLocaleString('en-US', { maximumFractionDigits: 3 })
}

/** "PCS · CTN×24 · PK6×6": the base unit, then the packs. */
export function packLabel(it: Pick<InvItem, 'base_unit' | 'units'>): string {
  return [it.base_unit, ...it.units.map((u) => `${u.unit}×${fmtQty(u.factor)}`)].join(' · ')
}

/**
 * 260 pieces with a 24-piece carton: "10 CTN + 20 PCS". How a storekeeper
 * counts, and how the item form shows each store's stock.
 */
export function inPacks(qty: string | number, it: Pick<InvItem, 'base_unit' | 'units'>): string {
  const n = Number(qty)
  const biggest: InvUnit | undefined = [...it.units].sort(
    (a, b) => Number(b.factor) - Number(a.factor))[0]
  if (!biggest || !Number.isFinite(n) || n <= 0 || Number(biggest.factor) <= 1) {
    return `${fmtQty(n)} ${it.base_unit}`
  }
  const f = Number(biggest.factor)
  const packs = Math.floor(n / f)
  const rest = +(n - packs * f).toFixed(3)
  if (!packs) return `${fmtQty(n)} ${it.base_unit}`
  return rest ? `${fmtQty(packs)} ${biggest.unit} + ${fmtQty(rest)} ${it.base_unit}`
    : `${fmtQty(packs)} ${biggest.unit}`
}

/** "12.345" -> [12345n, 3]: a decimal string as an integer and its scale. */
function scaled(s: string): [bigint, number] {
  const clean = (s ?? '').trim().replace(/,/g, '')
  if (!clean || clean === '.' || clean === '-') return [0n, 0]
  const neg = clean.startsWith('-')
  const [w = '0', f = ''] = (neg ? clean.slice(1) : clean).split('.')
  const v = BigInt((w || '0') + f)
  return [neg ? -v : v, f.length]
}

/**
 * qty x unit cost, rounded half-up to halalas, exactly as the server does
 * it (3 CTN at 38.4167 is 115.25, never 115.25000000000001). Strings in,
 * string out, no binary floats.
 */
export function lineAmount(qty: string, cost: string): string {
  const [q, qs] = scaled(qty)
  const [c, cs] = scaled(cost)
  const p = q * c
  const scale = qs + cs
  if (scale <= 2) {
    const v = p * 10n ** BigInt(2 - scale)
    return fmt2(v)
  }
  const div = 10n ** BigInt(scale - 2)
  const half = div / 2n
  return fmt2((p + (p >= 0n ? half : -half)) / div)
}

function fmt2(v: bigint): string {
  const neg = v < 0n
  const a = neg ? -v : v
  return `${neg ? '-' : ''}${a / 100n}.${(a % 100n).toString().padStart(2, '0')}`
}
